'use client';

import { useCallback, useState } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';

import { useBioUserAccess } from '../../../bio/contexts/user-access/useBioUserAccess';
import { compareExtendedSubscriptionLevel } from '../../../subscription/configuration/functions/subscription-display';
import { postViewModels } from '../functions/postViewModel';
import type { PostViewModel } from '../types';
import { useFetchPostViewInput } from './useFetchPostViewInput';

/** The viewer meets a subscribe gate when their level is at least the required one. */
function meetsLevel(
  viewer: PopStoreExtendedSubscriptionLevel | undefined,
  required: PopStoreExtendedSubscriptionLevel,
): boolean {
  return (
    viewer != null && compareExtendedSubscriptionLevel(viewer, required) >= 0
  );
}

/**
 * The visitor feed is served from an ANONYMOUS `'use cache'`, so a gated post is
 * `restricted` for every visitor — including a signed-in viewer who actually has
 * access. This hook refetches the post with the viewer's identity (the search API
 * redacts per requester) and swaps in the un-redacted view model when they're
 * entitled — mirroring legacy `reloadPost` in
 * `flows/bio/wall/components/post/index.tsx`.
 *
 * It only refetches when a reload could plausibly change the outcome (avoids a
 * wasted authed request):
 *   - **owner** → always (they own the content),
 *   - **pay-to-view** → always (entitlement is order-backed; the level can't tell us),
 *   - **crowd-tipping** → always (also order-backed, per creator),
 *   - **subscribe-to-view** → only when the viewer's level meets the required tier.
 *
 * Anonymous viewers (and posts that aren't restricted) never refetch — the
 * server-rendered view stands.
 */
export function useUnredactedPost(
  initial: PostViewModel,
  sellerId: string,
  bioContext: BioContext | null,
): { post: PostViewModel; reload: () => void } {
  const { currentUserId, extendedSubscriptionLevel } = useBioUserAccess();
  const loggedIn = currentUserId != null;
  const isOwner = loggedIn && currentUserId === sellerId;
  const { subscribe, pay, tip } = initial.gating;

  // Forced re-fetch after an order-backed unlock (pay / tip): the fetch inputs
  // don't change, so bump a key to re-run the effect. (Subscribe unlock is
  // already reactive — `extendedSubscriptionLevel` rising flips `shouldReload`.)
  const [reloadKey, setReloadKey] = useState(0);
  // Latches once an unlock has actually been requested — arms the retry-until-
  // unrestricted poll (the search index lags a purchase). A not-yet-entitled
  // probe (a pay post on first mount) leaves it false, so it fetches once.
  const [unlockRequested, setUnlockRequested] = useState(false);
  const reload = useCallback(() => {
    setUnlockRequested(true);
    setReloadKey(k => k + 1);
  }, []);

  const meetsSubscribe =
    subscribe != null && meetsLevel(extendedSubscriptionLevel, subscribe.level);
  const shouldReload =
    initial.restricted &&
    loggedIn &&
    (isOwner || pay != null || tip != null || meetsSubscribe);

  // Poll for un-restriction once an unlock is in play: an explicit pay/tip
  // reload, or the viewer's level having risen to meet a subscribe gate.
  const checkForUnlocked = unlockRequested || meetsSubscribe;

  const { input: fresh } = useFetchPostViewInput(
    shouldReload ? sellerId : undefined,
    shouldReload ? initial.id : undefined,
    reloadKey,
    checkForUnlocked,
  );

  if (!fresh) {
    return { post: initial, reload };
  }
  // Re-adapt the per-viewer post; the creator identity is unchanged, so rebuild
  // the author from the initial view model and PRESERVE its permalink (which the
  // page already built from the live bioContext — unchanged by unredaction). The
  // proxy already projected the raw row to `PostViewInput`.
  const author = {
    urlCode: initial.author.handle,
    storeFrontName: initial.author.name,
    avatar: initial.author.avatarUrl,
  };
  // The rebuilt permalink is discarded (overridden from `initial` below), but we
  // still thread the surface's REAL bioContext so no fabricated context leaks in.
  const [rebuilt] = postViewModels({
    posts: [fresh],
    author,
    bioContext,
  });
  return {
    post: rebuilt
      ? { ...rebuilt, permalinkHref: initial.permalinkHref }
      : initial,
    reload,
  };
}
